/**
 * "Tela em branco" board: free image/video nodes + manual edges.
 * image→image orders scenes, image→video ends a flow, video→video joins (source plays first).
 */

import type { FragmentadorPlan } from './fragmentadorPromptContract';
import { createEmptyPhase, type FragmentadorSessionPhase, type SessionRequestPlan } from './fragmentadorCompile';

/** Budget for the requests sent to the planner; the oldest drop first. */
export const REQUESTS_MAX_CHARS = 20000;

/** Append a request (skipping repeats) and keep the newest ones within budget. */
export function appendRequest(list: string[] | undefined, text: string): string[] {
  const t = String(text || '').trim();
  const out = (list || []).filter((r) => r.trim() && r.trim() !== t);
  if (t) out.push(t);
  return fitRequests(out);
}

export function fitRequests(list: string[]): string[] {
  const out = list.map((r) => r.trim()).filter(Boolean);
  let total = out.reduce((a, r) => a + r.length, 0);
  while (out.length > 1 && total > REQUESTS_MAX_CHARS) total -= out.shift()!.length;
  if (out.length === 1 && out[0].length > REQUESTS_MAX_CHARS) out[0] = out[0].slice(0, REQUESTS_MAX_CHARS);
  return out;
}

export type BoardAspect = '16:9' | '9:16' | '1:1' | '4:3' | '3:4';

export const BOARD_ASPECTS: BoardAspect[] = ['16:9', '9:16', '1:1', '4:3', '3:4'];

export const ASPECT_RATIOS: Record<BoardAspect, number> = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:3': 4 / 3, '3:4': 3 / 4 };

export function isBoardAspect(v: unknown): v is BoardAspect {
  return typeof v === 'string' && (BOARD_ASPECTS as string[]).includes(v);
}

/** Board aspect closest to a photo's shape (log distance, so 3:4 and 4:3 weigh the same). */
export function closestBoardAspect(width: number, height: number): BoardAspect {
  const r = width / height;
  if (!Number.isFinite(r) || r <= 0) return '16:9';
  return BOARD_ASPECTS.reduce((best, cur) =>
    Math.abs(Math.log(ASPECT_RATIOS[cur] / r)) < Math.abs(Math.log(ASPECT_RATIOS[best] / r)) ? cur : best,
  );
}

/** How a flow's scene images drive its video. */
export type BoardImageMode = 'keyframes' | 'reference';

export type BoardImageNode = {
  id: string;
  kind: 'image';
  x: number;
  y: number;
  /** Visual size, 0.45–1. Omitted at full size. */
  scale?: number;
  phase: FragmentadorSessionPhase;
  width?: number;
  height?: number;
  /** What the image shows, described once by the agent; valid while `captionUrl` is the current image. */
  caption?: string;
  captionUrl?: string;
  /** Every state of the image, oldest first: v1 is the image before the first edit. */
  versions?: BoardImageVersion[];
};

export type BoardImageVersion = {
  id: string;
  imageUrl: string;
  /** 0 for the image the node had before its first edit. */
  createdAt: number;
  width?: number;
  height?: number;
  /** What the user asked to change (absent on v1). */
  instruction?: string;
  imageModelId?: string;
  credits?: number;
};

export type BoardVideoStatus = 'idle' | 'generating' | 'joining' | 'ready' | 'failed';

/** One finished generation of a video node's flow. */
export type BoardVideoVersion = {
  id: string;
  clipUrl: string;
  /** 0 when unknown (clips generated before versions existed). */
  createdAt: number;
  videoModelId?: string;
  resolutionId?: string;
  seconds?: number;
  parts?: number;
  credits?: number;
  /** Script this generation was rendered from (absent on versions made before it was recorded). */
  brief?: string;
  scenes?: Array<{ label: string; description: string }>;
};

export type BoardVideoNode = {
  id: string;
  kind: 'video';
  x: number;
  y: number;
  /** Visual size, 0.45–1. Omitted at full size. */
  scale?: number;
  status: BoardVideoStatus;
  /** Active clip generated from this node's own image flow (one of `versions`). */
  clipUrl?: string;
  /** Every generation of this node's flow, oldest first. */
  versions?: BoardVideoVersion[];
  /** What the node plays: upstream joins + own clip. */
  videoUrl?: string;
  partUrls?: string[];
  partJobIds?: string[];
  progress?: string;
  error?: string;
  planned?: boolean;
  /** Explicit play order of upstream videos (set by "juntar" requests). */
  joinOrder?: string[];
  brief?: string;
  /** Every request the user made for this video, oldest first, verbatim (all binding; newest wins on conflict). */
  requests?: string[];
  /** The planner's reading of `requests`, carried into generation. */
  requestPlan?: SessionRequestPlan;
  /** Requests the planner could not fit into the storyboard. */
  uncovered?: string[];
  /**
   * Chain id + image URL the current script was written for.
   * A different arrow order or an edited photo makes the next video generation rewrite the script.
   */
  scriptBasis?: string;
  look?: FragmentadorPlan['look'];
  intentHint?: FragmentadorPlan['intent_hint'];
  totalDurationSeconds?: number;
  videoModelId?: string;
  resolutionId?: string;
  generateAudio?: boolean;
  /** Finished clip brought from the gallery: never generated, only joined. */
  fromGallery?: boolean;
  title?: string;
  /** Storyboard split at hard cuts: its flow nodes and their final join share this id. */
  flowGroup?: string;
  /** Set on "Vídeo editado" nodes: one upstream video + linked audio nodes, mixed into `videoUrl`. */
  edit?: BoardVideoEdit;
  /** How a join plays its direct inputs back to back. */
  joinTransition?: BoardJoinTransition;
  /**
   * keyframes (default): images chain into each other and the last feeds the video; one video call plays them in arrow order
   * (2 images = exact first/last frame, more = ordered references). A plan the model can't take in one video is split into flows + a join.
   * reference: every image links straight to the video (nothing between them) and guides style/place, with no position in time.
   */
  imageMode?: BoardImageMode;
  /** Inputs chosen on the generator card (model lives in videoModelId / resolutionId / generateAudio). */
  generator?: BoardGenerator;
  /** Explore "Criar igual" job already queued for this card. The page waits on it instead of starting a new render. */
  remixJobId?: string;
  /** Job whose prompt generated this card. Stays after the clip is ready so a later request can revise that prompt. */
  templateJobId?: string;
};

/** A file the user attached on the generator card. */
export type BoardMediaRef = { url: string; label?: string };

export type GeneratorSlot = 'start' | 'end' | 'attachment' | 'video' | 'audio';

export type BoardGenerator = {
  /** Text in the card's prompt box: the user's own words, or the full prompt of the current script. */
  prompt?: string;
  /** user = typed on the card (the agent plans from it on generate); script = written from the storyboard. */
  promptSource?: 'user' | 'script';
  attachments?: BoardMediaRef[];
  startImage?: BoardMediaRef;
  endImage?: BoardMediaRef;
  referenceVideo?: BoardMediaRef;
  audio?: BoardMediaRef;
  /** Variations shown on the card. Generation still renders one clip. */
  count?: number;
  music?: boolean;
  draft?: boolean;
  /** Explore remix: the creator prompt stays off the card. */
  hidePrompt?: boolean;
  /**
   * Skip the storyboard chain and render as V2V from `referenceVideo`
   * (e.g. replace a complex background with a plate photo).
   */
  v2vOnly?: boolean;
  /** Options frozen by the template. Locked controls stay disabled after generation too. */
  locks?: {
    model?: boolean;
    resolution?: boolean;
    duration?: boolean;
    audio?: boolean;
    aspect?: boolean;
  };
};

export function generatorHasImages(node: BoardVideoNode): boolean {
  const g = node.generator;
  return Boolean(g?.startImage?.url || g?.endImage?.url || g?.attachments?.some((a) => a.url));
}

/** fade = every input fades in from and out to black; crossfade = consecutive inputs dissolve into each other. */
export type BoardJoinTransition = {
  kind: 'fade' | 'crossfade';
  seconds: number;
};

export type BoardVideoEdit = {
  /** Volume of the upstream video's own audio (0 = mute, 1 = original). */
  sourceVolume: number;
  /** Burn the narration text as subtitles. */
  captions?: boolean;
  captionPosition?: 'top' | 'center' | 'bottom';
  /** Fade in from black and out to black. */
  fade?: boolean;
  /** Cinematic color grade (warm/cool curve — still in color). */
  grade?: boolean;
  /** True black-and-white (desaturate). Mutually exclusive with cinematic grade in the mixer. */
  grayscale?: boolean;
};

export type BoardAudioKind = 'voice' | 'music' | 'sfx';
export type BoardAudioStatus = 'idle' | 'generating' | 'ready' | 'failed';

export type BoardAudioVersion = {
  id: string;
  audioUrl: string;
  createdAt: number;
  text: string;
  voiceModelId?: string;
  voiceId?: string;
  seconds?: number;
  credits?: number;
};

/** A sound track (narration, music or effect) feeding a "Vídeo editado" node. */
export type BoardAudioNode = {
  id: string;
  kind: 'audio';
  x: number;
  y: number;
  /** Visual size, 0.45–1. Omitted at full size. */
  scale?: number;
  audioKind: BoardAudioKind;
  title?: string;
  /** Voice: the spoken script. Music / effect: the style or sound description. */
  text: string;
  voiceModelId?: string;
  voiceId?: string;
  /** Add Instrumental: the voice node whose audio gets the accompaniment. */
  sourceAudioId?: string;
  status: BoardAudioStatus;
  progress?: string;
  error?: string;
  audioUrl?: string;
  seconds?: number;
  versions?: BoardAudioVersion[];
  /** 0–1.5, 1 = as generated. */
  volume: number;
  /** Seconds into the edited video where the track starts. */
  startOffset: number;
  loop?: boolean;
  speed?: number;
  muted?: boolean;
};

/** A note on the board. It sits in the flow visually and never feeds a video. */
export type BoardCommentNode = {
  id: string;
  kind: 'comment';
  x: number;
  y: number;
  /** Visual size, 0.45–1. Omitted at full size. Unused by the comment box; the box uses `w`/`h`. */
  scale?: number;
  text: string;
  /** Box size in world pixels. Omitted at the default size. */
  w?: number;
  h?: number;
};

export type BoardNode = BoardImageNode | BoardVideoNode | BoardAudioNode | BoardCommentNode;
/**
 * `flow` = seta do vídeo (padrão, contínua). Entra na ordem do roteiro.
 * `ref` = seta de referência (tracejada). A imagem de origem serviu para gerar outra
 * imagem, ou é uma placa de fundo; não entra no vídeo.
 * O mesmo par pode ter as duas.
 */
export type BoardEdgeRole = 'flow' | 'ref';
export type BoardEdge = { id: string; from: string; to: string; role?: BoardEdgeRole };

export function isFlowEdge(e: BoardEdge): boolean {
  return e.role !== 'ref';
}

export type Board = {
  nodes: BoardNode[];
  edges: BoardEdge[];
  aspectRatio: BoardAspect;
  /** Picked by the user or agent; otherwise the first photo's shape sets the format. */
  aspectChosen?: boolean;
  imageModelId?: string;
};

export const IMAGE_NODE_W = 224;
export const IMAGE_NODE_H = 168;
export const VIDEO_NODE_W = 300;
/** Picture area is 16:9; the footer sits under it. Padding 12+12, gap 10, footer 20. */
const VIDEO_CHROME_H = 54;
export const VIDEO_NODE_H = VIDEO_CHROME_H + Math.round((VIDEO_NODE_W - 24) * 9 / 16);
/** Magnific-style generator card. The picture covers the whole card; the prompt and chips sit on top of it. */
export const GENERATOR_NODE_W = 360;
/** Title row above the stage: padding 10+10, gap 8, title line 20. */
const GENERATOR_CHROME_H = 48;
/** Room under the 16:9 frame so the overlaid controls do not cover the whole picture. */
export const GENERATOR_PANEL_H = 108;
export function generatorNodeSize(aspect: BoardAspect = '16:9'): { w: number; h: number } {
  const ratio = ASPECT_RATIOS[aspect] || (16 / 9);
  const frameH = Math.round((GENERATOR_NODE_W - 20) / ratio);
  return { w: GENERATOR_NODE_W, h: GENERATOR_CHROME_H + frameH + GENERATOR_PANEL_H };
}
export const GENERATOR_NODE_H = generatorNodeSize('16:9').h;
export const AUDIO_NODE_W = 264;
export const AUDIO_NODE_H = 132;
export const COMMENT_NODE_W = 260;
export const COMMENT_NODE_H = 148;
export const COMMENT_MIN_W = 180;
export const COMMENT_MIN_H = 108;
export const COMMENT_MAX_W = 520;
export const COMMENT_MAX_H = 420;
export const COMMENT_TEXT_MAX = 4000;

export function clampCommentBox(w: number, h: number): { w: number; h: number } {
  const width = Number.isFinite(w) ? w : COMMENT_NODE_W;
  const height = Number.isFinite(h) ? h : COMMENT_NODE_H;
  return {
    w: Math.round(Math.min(COMMENT_MAX_W, Math.max(COMMENT_MIN_W, width))),
    h: Math.round(Math.min(COMMENT_MAX_H, Math.max(COMMENT_MIN_H, height))),
  };
}
export const NODE_GAP_X = 72;
export const NODE_GAP_Y = 48;
const LAYOUT_PAD = 80;

/** Right-center of `a` to left-center of `b`, with a horizontal cubic bend. */
export function edgeAnchors(a: BoardNode, b: BoardNode, aspect: BoardAspect = '16:9') {
  const sa = nodeSize(a, aspect);
  const sb = nodeSize(b, aspect);
  const x1 = a.x + sa.w;
  const y1 = a.y + sa.h / 2;
  const x2 = b.x;
  const y2 = b.y + sb.h / 2;
  const dx = Math.max(40, Math.abs(x2 - x1) * 0.45);
  return { x1, y1, c1x: x1 + dx, c1y: y1, c2x: x2 - dx, c2y: y2, x2, y2 };
}

function layoutNeighbors(board: Board, id: string): string[] {
  const out: string[] = [];
  for (const e of board.edges) {
    if (e.from === id) out.push(e.to);
    else if (e.to === id) out.push(e.from);
  }
  return out;
}

function layoutComponents(board: Board): string[][] {
  const seen = new Set<string>();
  const comps: string[][] = [];
  for (const n of board.nodes) {
    if (seen.has(n.id)) continue;
    const ids: string[] = [];
    const q = [n.id];
    seen.add(n.id);
    while (q.length) {
      const id = q.shift()!;
      ids.push(id);
      for (const nb of layoutNeighbors(board, id)) {
        if (!seen.has(nb) && nodeById(board, nb)) {
          seen.add(nb);
          q.push(nb);
        }
      }
    }
    comps.push(ids);
  }
  const centroid = (ids: string[]) => {
    let x = 0;
    let y = 0;
    for (const id of ids) {
      const n = nodeById(board, id);
      if (!n) continue;
      x += n.x;
      y += n.y;
    }
    return { x: x / ids.length, y: y / ids.length };
  };
  return comps.sort((a, b) => {
    const ca = centroid(a);
    const cb = centroid(b);
    return ca.y - cb.y || ca.x - cb.x;
  });
}

function layoutComponent(board: Board, ids: string[]): Map<string, { x: number; y: number }> {
  const inComp = new Set(ids);
  const pos = new Map<string, { x: number; y: number }>();
  const place = (id: string, x: number, y: number) => {
    if (!inComp.has(id) || pos.has(id)) return;
    pos.set(id, { x: Math.round(x), y: Math.round(y) });
  };
  const images = ids.map((id) => nodeById(board, id)).filter(isImage);
  const videos = readingOrder(ids.map((id) => nodeById(board, id)).filter(isVideo));
  const audios = ids.map((id) => nodeById(board, id)).filter(isAudio)
    .sort((a, b) => a.y - b.y || a.x - b.x);

  let rowY = 0;
  for (const v of videos) {
    const chain = chainForVideo(board, v.id).filter((n) => inComp.has(n.id) && !pos.has(n.id));
    const extras = images.filter((n) =>
      !pos.has(n.id) && !chain.some((c) => c.id === n.id)
      && board.edges.some((e) => e.from === n.id && e.to === v.id));
    const row = [...chain, ...readingOrder(extras)];
    if (!row.length) continue;
    const vSize = nodeSize(v, board.aspectRatio);
    const direct = row.filter((n) => board.edges.some((e) => e.from === n.id && e.to === v.id)).length;
    // Several images feeding the video on one row would draw their lines through the images between them.
    const stacked = v.imageMode === 'reference' || direct > 1;
    if (stacked) {
      const stackStep = IMAGE_NODE_H + NODE_GAP_Y;
      const stackTop = rowY;
      for (let i = 0; i < row.length; i++) place(row[i].id, 0, stackTop + i * stackStep);
      const stackH = IMAGE_NODE_H + (row.length - 1) * stackStep;
      const videoY = rowY + Math.max(0, (stackH - vSize.h) / 2);
      place(v.id, IMAGE_NODE_W + NODE_GAP_X, videoY);
      rowY = Math.max(rowY + stackH, videoY + vSize.h) + NODE_GAP_Y * 2;
    } else {
      const step = IMAGE_NODE_W + NODE_GAP_X;
      const videoY = rowY + Math.max(0, (IMAGE_NODE_H - vSize.h) / 2);
      for (let i = 0; i < row.length; i++) place(row[i].id, i * step, rowY);
      place(v.id, row.length * step, videoY);
      rowY = Math.max(rowY + IMAGE_NODE_H, videoY + vSize.h) + NODE_GAP_Y * 2;
    }
  }

  const leftover = readingOrder(images.filter((n) => !pos.has(n.id)));
  if (leftover.length) {
    const step = IMAGE_NODE_W + NODE_GAP_X;
    leftover.forEach((n, i) => place(n.id, i * step, rowY));
    rowY += IMAGE_NODE_H + NODE_GAP_Y * 2;
  }

  const pending = videos.filter((v) => !pos.has(v.id));
  while (pending.length) {
    const idx = pending.findIndex((v) =>
      upstreamVideos(board, v.id).filter((u) => inComp.has(u.id)).every((u) => pos.has(u.id)));
    const v = pending.splice(idx >= 0 ? idx : 0, 1)[0];
    const vSize = nodeSize(v, board.aspectRatio);
    const ups = upstreamVideos(board, v.id).filter((u) => pos.has(u.id));
    if (ups.length) {
      const right = Math.max(...ups.map((u) => pos.get(u.id)!.x + nodeSize(u, board.aspectRatio).w));
      // A narrower video sitting left of a wider sibling would put its line through that sibling.
      for (const u of ups) {
        const p = pos.get(u.id);
        if (!p) continue;
        p.x = right - nodeSize(u, board.aspectRatio).w;
      }
      const mid = ups.reduce((sum, u) => sum + pos.get(u.id)!.y + nodeSize(u, board.aspectRatio).h / 2, 0) / ups.length;
      const videoY = Math.max(0, mid - vSize.h / 2);
      place(v.id, right + NODE_GAP_X, videoY);
    } else {
      place(v.id, 0, rowY);
      rowY += vSize.h + NODE_GAP_Y * 2;
    }
  }

  const audioByTarget = new Map<string, BoardAudioNode[]>();
  const looseAudio: BoardAudioNode[] = [];
  for (const a of audios) {
    const target = board.edges.map((e) => (e.from === a.id ? e.to : '')).find((id) => id && pos.has(id));
    if (!target) {
      looseAudio.push(a);
      continue;
    }
    const list = audioByTarget.get(target) || [];
    list.push(a);
    audioByTarget.set(target, list);
  }
  audioByTarget.forEach((list, target) => {
    const t = pos.get(target);
    const host = nodeById(board, target);
    if (!t || !host) return;
    const th = nodeSize(host, board.aspectRatio).h;
    const ax = t.x - AUDIO_NODE_W - NODE_GAP_X;
    for (let i = 0; i < list.length; i++) {
      place(list[i].id, ax, t.y + th + NODE_GAP_Y + i * (AUDIO_NODE_H + NODE_GAP_Y));
    }
  });
  looseAudio.forEach((a, i) => place(a.id, 0, rowY + i * (AUDIO_NODE_H + NODE_GAP_Y)));

  for (const id of ids) {
    if (!pos.has(id)) place(id, 0, rowY);
  }
  return pos;
}

const LAYOUT_CLEAR = 12;

function boxesOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  pad: number,
): boolean {
  return a.x < b.x + b.w + pad && b.x < a.x + a.w + pad && a.y < b.y + b.h + pad && b.y < a.y + a.h + pad;
}

function curvePoint(c: ReturnType<typeof edgeAnchors>, t: number): { x: number; y: number } {
  const u = 1 - t;
  return {
    x: u * u * u * c.x1 + 3 * u * u * t * c.c1x + 3 * u * t * t * c.c2x + t * t * t * c.x2,
    y: u * u * u * c.y1 + 3 * u * u * t * c.c1y + 3 * u * t * t * c.c2y + t * t * t * c.y2,
  };
}

function orient(a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsCross(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
  d: { x: number; y: number },
): boolean {
  const ab = orient(a, b, c) * orient(a, b, d);
  const cd = orient(c, d, a) * orient(c, d, b);
  if (ab === 0 && cd === 0) {
    const hit = (p: { x: number; y: number }, q: { x: number; y: number }, r: { x: number; y: number }) =>
      r.x >= Math.min(p.x, q.x) && r.x <= Math.max(p.x, q.x) && r.y >= Math.min(p.y, q.y) && r.y <= Math.max(p.y, q.y);
    return hit(a, b, c) || hit(a, b, d) || hit(c, d, a) || hit(c, d, b);
  }
  return ab <= 0 && cd <= 0;
}

function segmentHitsRect(
  a: { x: number; y: number },
  b: { x: number; y: number },
  r: { x: number; y: number; w: number; h: number },
): boolean {
  const inside = (p: { x: number; y: number }) => p.x > r.x && p.x < r.x + r.w && p.y > r.y && p.y < r.y + r.h;
  if (inside(a) || inside(b)) return true;
  const corners = [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
  return corners.some((corner, i) => segmentsCross(a, b, corner, corners[(i + 1) % 4]));
}

function curveHitsBox(c: ReturnType<typeof edgeAnchors>, box: { x: number; y: number; w: number; h: number }): boolean {
  const inset = { x: box.x + 1, y: box.y + 1, w: box.w - 2, h: box.h - 2 };
  if (inset.w <= 0 || inset.h <= 0) return false;
  let prev = curvePoint(c, 0);
  for (let i = 1; i <= 40; i++) {
    const p = curvePoint(c, i / 40);
    if (segmentHitsRect(prev, p, inset)) return true;
    prev = p;
  }
  return false;
}

/** Push overlapping nodes down, then move any node a line still crosses below that line. */
function untangle(board: Board, pos: Map<string, { x: number; y: number }>): void {
  const aspect = board.aspectRatio;
  const boxes = () => board.nodes.flatMap((n) => {
    const p = pos.get(n.id);
    if (!p) return [];
    const s = nodeSize(n, aspect);
    return [{ id: n.id, x: p.x, y: p.y, w: s.w, h: s.h }];
  });
  for (let iter = 0; iter < 20; iter++) {
    let moved = false;
    const list = boxes().sort((a, b) => a.y - b.y || a.x - b.x);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const b = list[j];
        if (!boxesOverlap(a, b, LAYOUT_CLEAR)) continue;
        const push = a.y + a.h + LAYOUT_CLEAR - b.y;
        if (push > 0) {
          const p = pos.get(b.id);
          if (!p) continue;
          p.y += push;
          b.y += push;
          moved = true;
        }
      }
    }
    const placed = (id: string) => {
      const n = nodeById(board, id);
      const p = pos.get(id);
      return n && p ? { ...n, x: p.x, y: p.y } : undefined;
    };
    for (const edge of board.edges) {
      const from = placed(edge.from);
      const to = placed(edge.to);
      if (!from || !to) continue;
      const curve = edgeAnchors(from, to, aspect);
      const low = Math.max(curve.y1, curve.y2);
      for (const box of boxes()) {
        if (box.id === edge.from || box.id === edge.to) continue;
        if (!curveHitsBox(curve, box)) continue;
        const push = low + LAYOUT_CLEAR - box.y;
        if (push > 0) {
          const p = pos.get(box.id);
          if (!p) continue;
          p.y += push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  pos.forEach((p) => {
    p.x = Math.round(p.x);
    p.y = Math.round(p.y);
  });
}

/**
 * Lines each connected flow left-to-right (images → video → join → edit, audio beside the edit)
 * and stacks separate flows top-to-bottom. Lines stay in the gaps. Does not change edges.
 * Comment boxes stay where they were placed.
 */
export function layoutBoard(board: Board): Board {
  const pinned = board.nodes.some((n) => n.kind === 'comment');
  const flow = pinned ? { ...board, nodes: board.nodes.filter((n) => n.kind !== 'comment') } : board;
  const laid = layoutFlow(flow);
  if (!pinned) return laid;
  if (laid === flow) return board;
  const moved = new Map(laid.nodes.map((n) => [n.id, n]));
  let changed = false;
  const nodes = board.nodes.map((n) => {
    const next = moved.get(n.id);
    if (!next || (next.x === n.x && next.y === n.y)) return n;
    changed = true;
    return next;
  });
  return changed ? { ...board, nodes } : board;
}

function layoutFlow(board: Board): Board {
  if (!board.nodes.length) return board;
  const next = new Map<string, { x: number; y: number }>();
  let top = LAYOUT_PAD;
  for (const ids of layoutComponents(board)) {
    const local = layoutComponent(board, ids);
    let minX = Infinity;
    let minY = Infinity;
    let maxBottom = -Infinity;
    for (const id of ids) {
      const p = local.get(id);
      const n = nodeById(board, id);
      if (!p || !n) continue;
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxBottom = Math.max(maxBottom, p.y + nodeSize(n, board.aspectRatio).h);
    }
    if (!Number.isFinite(minX)) continue;
    const dx = LAYOUT_PAD - minX;
    const dy = top - minY;
    for (const id of ids) {
      const p = local.get(id);
      if (p) next.set(id, { x: p.x + dx, y: p.y + dy });
    }
    top += (maxBottom - minY) + NODE_GAP_Y * 2;
  }
  untangle(board, next);
  let changed = false;
  const nodes = board.nodes.map((n) => {
    const p = next.get(n.id);
    if (!p || (p.x === n.x && p.y === n.y)) return n;
    changed = true;
    return { ...n, x: p.x, y: p.y };
  });
  return changed ? { ...board, nodes } : board;
}

/** How far a node can shrink. Full size is 1; larger values are ignored. */
export const NODE_SCALE_MIN = 0.45;
export const NODE_SCALE_MAX = 1;

export function nodeScale(node: BoardNode): number {
  const s = node.scale;
  if (typeof s !== 'number' || !Number.isFinite(s)) return 1;
  return Math.min(NODE_SCALE_MAX, Math.max(NODE_SCALE_MIN, s));
}

/** Snap a drag to the stored scale. 1 means "full size" (the field stays unset). */
export function clampNodeScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  const clamped = Math.min(NODE_SCALE_MAX, Math.max(NODE_SCALE_MIN, scale));
  const rounded = Math.round(clamped * 100) / 100;
  return rounded >= 0.995 ? 1 : rounded;
}

/** Designed box, before `scale`. The card lays out at this size and is then scaled. */
export function nodeFrame(node: BoardNode, aspect: BoardAspect = '16:9'): { w: number; h: number } {
  if (node.kind === 'video') {
    // Edit cards share the generator chrome (head + stage); gallery clips stay compact.
    if (node.edit || (!node.fromGallery)) return generatorNodeSize(aspect);
    return { w: VIDEO_NODE_W, h: VIDEO_NODE_H };
  }
  if (node.kind === 'audio') return { w: AUDIO_NODE_W, h: AUDIO_NODE_H };
  if (node.kind === 'comment') return clampCommentBox(node.w ?? COMMENT_NODE_W, node.h ?? COMMENT_NODE_H);
  return { w: IMAGE_NODE_W, h: IMAGE_NODE_H };
}

/** Box used by layout, edges and hit tests: the frame multiplied by the node scale. */
export function nodeSize(node: BoardNode, aspect: BoardAspect = '16:9'): { w: number; h: number } {
  const frame = nodeFrame(node, aspect);
  const s = nodeScale(node);
  if (s === 1) return frame;
  return { w: Math.round(frame.w * s), h: Math.round(frame.h * s) };
}

export function emptyBoard(): Board {
  return { nodes: [], edges: [], aspectRatio: '16:9' };
}

export function newId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function createImageNode(imageUrl: string, x: number, y: number, label: string): BoardImageNode {
  const base = createEmptyPhase(0);
  return {
    id: base.id,
    kind: 'image',
    x,
    y,
    phase: {
      ...base,
      label,
      description_pt: label,
      still_prompt_en: '',
      status: 'ready',
      imageUrl,
    },
  };
}

export function createVideoNode(x: number, y: number): BoardVideoNode {
  return { id: newId('video'), kind: 'video', x, y, status: 'idle' };
}

export function createGalleryVideoNode(url: string, x: number, y: number, title: string): BoardVideoNode {
  return { ...createVideoNode(x, y), status: 'ready', clipUrl: url, videoUrl: url, fromGallery: true, title };
}

export function createEditNode(x: number, y: number): BoardVideoNode {
  return { ...createVideoNode(x, y), title: 'Vídeo editado', edit: { sourceVolume: 1 } };
}

const DEFAULT_AUDIO_VOLUME: Record<BoardAudioKind, number> = { voice: 1, music: 0.35, sfx: 0.8 };
const AUDIO_TITLES: Record<BoardAudioKind, string> = { voice: 'Locução', music: 'Música de fundo', sfx: 'Efeito sonoro' };

export function createCommentNode(x: number, y: number, text = ''): BoardCommentNode {
  return {
    id: newId('comment'),
    kind: 'comment',
    x,
    y,
    text: String(text || '').slice(0, COMMENT_TEXT_MAX),
  };
}

export function createAudioNode(x: number, y: number, audioKind: BoardAudioKind, text: string): BoardAudioNode {
  return {
    id: newId('audio'),
    kind: 'audio',
    x,
    y,
    audioKind,
    title: AUDIO_TITLES[audioKind],
    text,
    status: 'idle',
    volume: DEFAULT_AUDIO_VOLUME[audioKind],
    startOffset: 0,
  };
}

export function imageVersions(node: BoardImageNode): BoardImageVersion[] {
  return node.versions || [];
}

/** Record a new state of the image; the image before the first change is kept as v1. Apply `imageUrl` to the phase too. */
export function withNewImageVersion(node: BoardImageNode, version: Omit<BoardImageVersion, 'id'>): Partial<BoardImageNode> {
  const prior = imageVersions(node);
  const base = prior.length || !node.phase.imageUrl
    ? prior
    : [{ id: newId('iver'), imageUrl: node.phase.imageUrl, createdAt: 0, width: node.width, height: node.height }];
  return {
    versions: [...base, { ...version, id: newId('iver') }],
    ...(version.width && version.height ? { width: version.width, height: version.height } : {}),
  };
}

export function audioVersions(node: BoardAudioNode): BoardAudioVersion[] {
  return node.versions || [];
}

/** Record a finished generation and make it the node's active track. */
export function withNewAudioVersion(node: BoardAudioNode, version: Omit<BoardAudioVersion, 'id'>): Partial<BoardAudioNode> {
  return {
    versions: [...audioVersions(node), { ...version, id: newId('aver') }],
    audioUrl: version.audioUrl,
    seconds: version.seconds,
  };
}

/** Audio nodes linked into a "Vídeo editado" node, top to bottom. */
export function audioInputs(board: Board, videoId: string): BoardAudioNode[] {
  return board.edges
    .filter((e) => e.to === videoId)
    .map((e) => nodeById(board, e.from))
    .filter(isAudio)
    .sort((a, b) => a.y - b.y || a.x - b.x);
}

/** The "Vídeo editado" node an audio node feeds. */
export function editNodeForAudio(board: Board, audioId: string): BoardVideoNode | undefined {
  return board.edges
    .filter((e) => e.from === audioId)
    .map((e) => nodeById(board, e.to))
    .find((n): n is BoardVideoNode => isVideo(n) && Boolean(n.edit));
}

/** The "Vídeo editado" node already fed by this video, if any. */
export function editNodeForVideo(board: Board, videoId: string): BoardVideoNode | undefined {
  const node = nodeById(board, videoId);
  if (isVideo(node) && node.edit) return node;
  return downstreamVideos(board, videoId).find((d) => Boolean(d.edit));
}

/** Seconds a video node plays: its flow's last scene end plus what its upstream videos play. */
export function videoSeconds(board: Board, videoId: string, seen = new Set<string>()): number {
  if (seen.has(videoId)) return 0;
  seen.add(videoId);
  const node = nodeById(board, videoId);
  if (!isVideo(node)) return 0;
  const ups = upstreamVideos(board, videoId);
  if (node.edit) return ups[0] ? videoSeconds(board, ups[0].id, seen) : 0;
  const own = chainForVideo(board, videoId).reduce((max, n) => Math.max(max, n.phase.t_end || 0), 0)
    || (node.fromGallery || node.versions?.length ? node.totalDurationSeconds || 0 : 0);
  const total = own + ups.reduce((sum, u) => sum + videoSeconds(board, u.id, seen), 0);
  const cuts = ups.length + (own > 0 ? 1 : 0) - 1;
  return node.joinTransition?.kind === 'crossfade' && cuts > 0
    ? Math.max(0, total - cuts * node.joinTransition.seconds)
    : total;
}

/** Generations of a node's flow, oldest first; nodes saved before versions existed expose their clip as v1. */
export function videoVersions(node: BoardVideoNode): BoardVideoVersion[] {
  if (node.fromGallery) return [];
  if (node.versions?.length) return node.versions;
  if (!node.clipUrl) return [];
  return [{
    id: `legacy_${node.id}`,
    clipUrl: node.clipUrl,
    createdAt: 0,
    videoModelId: node.videoModelId,
    resolutionId: node.resolutionId,
    seconds: node.totalDurationSeconds,
  }];
}

/** Record a finished generation and make it the node's active clip. */
export function withNewVideoVersion(node: BoardVideoNode, version: Omit<BoardVideoVersion, 'id'>): Partial<BoardVideoNode> {
  return { versions: [...videoVersions(node), { ...version, id: newId('ver') }], clipUrl: version.clipUrl };
}

export type ProjectGeneration = {
  videoId: string;
  /** `join`: what a join node currently plays (no history of its own). */
  kind: 'flow' | 'join';
  flowLabel: string;
  version: BoardVideoVersion;
  versionNumber: number;
  active: boolean;
  joinCount?: number;
  /** Set on "Vídeo editado" entries: sound tracks mixed in. */
  audioCount?: number;
};

/** What each join plays, then every generation across the project's flows (newest first per flow). */
export function projectGenerations(board: Board): ProjectGeneration[] {
  const hasChain = (n: BoardVideoNode) => (chainForVideo(board, n.id).length > 0 ? 0 : 1);
  const videos = board.nodes
    .filter((n): n is BoardVideoNode => n.kind === 'video' && !n.fromGallery)
    .sort((a, b) => hasChain(a) - hasChain(b) || a.x - b.x || a.y - b.y);
  const flows = videos.filter((n) => videoVersions(n).length > 0);
  const out: ProjectGeneration[] = flows.flatMap((node, flowIndex) => {
    const versions = videoVersions(node);
    const first = chainForVideo(board, node.id)[0];
    const flowLabel = node.title || first?.phase.label || `Fluxo ${flowIndex + 1}`;
    return versions
      .map((version, i) => ({
        videoId: node.id,
        kind: 'flow' as const,
        flowLabel,
        version,
        versionNumber: i + 1,
        active: version.clipUrl === node.clipUrl,
      }))
      .reverse();
  });
  const joins: ProjectGeneration[] = [];
  for (const node of videos) {
    const ups = upstreamVideos(board, node.id);
    if (!ups.length || chainForVideo(board, node.id).length || !node.videoUrl) continue;
    joins.push({
      videoId: node.id,
      kind: 'join',
      flowLabel: node.title || (node.edit ? 'Vídeo editado' : 'Vídeo final'),
      version: { id: `join_${node.id}`, clipUrl: node.videoUrl, createdAt: 0 },
      versionNumber: 1,
      active: true,
      joinCount: ups.length,
      ...(node.edit ? { audioCount: audioInputs(board, node.id).length } : {}),
    });
  }
  return [...joins.sort((a, b) => Number(b.audioCount != null) - Number(a.audioCount != null)), ...out];
}

/** Nodes of a storyboard split at hard cuts: flows left→right plus the join that plays them in order. */
export function flowGroupNodes(board: Board, groupId: string): { flows: BoardVideoNode[]; join?: BoardVideoNode } {
  const nodes = board.nodes.filter((n): n is BoardVideoNode => n.kind === 'video' && n.flowGroup === groupId);
  return {
    flows: nodes.filter((n) => chainForVideo(board, n.id).length > 0).sort((a, b) => a.x - b.x || a.y - b.y),
    join: nodes.find((n) => chainForVideo(board, n.id).length === 0),
  };
}

/** The node a "generate" on this flow should finish at: its group's join, or itself. */
export function finalVideoFor(board: Board, videoId: string): string {
  const node = nodeById(board, videoId);
  if (!isVideo(node) || !node.flowGroup) return videoId;
  return flowGroupNodes(board, node.flowGroup).join?.id || videoId;
}

/**
 * Flow nodes a "generate" on this node renders. A flow renders itself (plus upstream flows still
 * missing a clip); a pure join renders the flows missing a clip, or all of them when none are missing.
 */
export function generationTargets(board: Board, videoId: string): BoardVideoNode[] {
  const flows: BoardVideoNode[] = [];
  const seen = new Set<string>();
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    for (const up of upstreamVideos(board, id)) visit(up.id);
    const n = nodeById(board, id);
    if (
      isVideo(n) && !n.fromGallery && !n.edit
      && (chainForVideo(board, id).length > 0 || generatorHasImages(n) || Boolean(n.generator?.referenceVideo?.url))
    ) flows.push(n);
  };
  visit(videoId);
  const self = flows.find((f) => f.id === videoId);
  const missing = flows.filter((f) => !f.clipUrl && f.id !== videoId);
  if (self) return [...missing, self];
  return missing.length ? missing : flows;
}

/** Whether the agent has anything to build from: a ready image, or two ready videos to join. */
export function boardHasSource(board: Board): boolean {
  const images = board.nodes.filter(isImage).some((n) => Boolean(n.phase.imageUrl) && n.phase.status === 'ready');
  const videos = board.nodes.filter(isVideo).filter((n) => Boolean(n.videoUrl)).length;
  return images || videos >= 2;
}

export function isImage(n: BoardNode | undefined): n is BoardImageNode {
  return n?.kind === 'image';
}

export function isVideo(n: BoardNode | undefined): n is BoardVideoNode {
  return n?.kind === 'video';
}

export function isAudio(n: BoardNode | undefined): n is BoardAudioNode {
  return n?.kind === 'audio';
}

export function isComment(n: BoardNode | undefined): n is BoardCommentNode {
  return n?.kind === 'comment';
}

export function nodeById(board: Board, id: string): BoardNode | undefined {
  return board.nodes.find((n) => n.id === id);
}

function reaches(board: Board, from: string, target: string, seen = new Set<string>()): boolean {
  if (from === target) return true;
  if (seen.has(from)) return false;
  seen.add(from);
  return board.edges.some((e) => isFlowEdge(e) && e.from === from && reaches(board, e.to, target, seen));
}

export function canConnect(board: Board, from: string, to: string, role: BoardEdgeRole = 'flow'): string | null {
  if (from === to) return 'Não dá para ligar um nó nele mesmo.';
  const a = nodeById(board, from);
  const b = nodeById(board, to);
  if (!a || !b) return 'Nó não encontrado.';
  if (isComment(a) || isComment(b)) return 'O comentário fica no quadro e não liga no fluxo.';
  if (role === 'ref') {
    const imageToImage = isImage(a) && isImage(b);
    const plate = isImage(a) && isVideo(b) && !b.edit && !b.fromGallery;
    if (!imageToImage && !plate) {
      return 'Ligação de referência vai de uma imagem para outra imagem, ou para um vídeo de fluxo.';
    }
    if (board.edges.some((e) => e.role === 'ref' && e.from === from && e.to === to)) {
      return 'Esses nós já estão ligados por referência.';
    }
    if (reaches(board, to, from)) return 'Essa seta criaria um ciclo.';
    return null;
  }
  if (isAudio(b)) return 'O som é uma faixa de entrada — ligue-o a um “Vídeo editado”.';
  if (isAudio(a) && !(isVideo(b) && b.edit)) return 'Som só liga num nó “Vídeo editado”.';
  if (a.kind === 'video' && b.kind === 'image') return 'Vídeo só liga em outro vídeo (para juntar).';
  if (a.kind === 'image' && isVideo(b) && b.fromGallery) return 'Vídeo da galeria já está pronto — ligue-o a outro vídeo para juntar.';
  if (a.kind === 'image' && isVideo(b) && b.edit) return 'O vídeo editado recebe um vídeo e sons, não imagens.';
  if (board.edges.some((e) => isFlowEdge(e) && e.from === from && e.to === to)) return 'Esses nós já estão ligados.';
  if (reaches(board, to, from)) return 'Essa seta criaria um ciclo.';
  return null;
}

/**
 * Adds an edge. A keyframe image→video link replaces the video's previous image input;
 * a reference-mode video keeps every image linked straight to it. A video→edit link replaces its source.
 * `role: 'ref'` is the dashed reference link (image→image generation source, or a background plate).
 * It does not replace a flow link, and a flow link does not replace it.
 */
export function connect(board: Board, from: string, to: string, role: BoardEdgeRole = 'flow'): Board {
  if (canConnect(board, from, to, role)) return board;
  const a = nodeById(board, from);
  const b = nodeById(board, to);
  if (role === 'ref') {
    return { ...board, edges: [...board.edges, { id: newId('edge'), from, to, role: 'ref' }] };
  }
  let edges = board.edges;
  const reference = isImage(a) && isVideo(b) && b.imageMode === 'reference';
  if (isImage(a) && isVideo(b) && !reference) {
    edges = edges.filter((e) => !(isFlowEdge(e) && e.to === to && isImage(nodeById(board, e.from))));
  }
  if (isVideo(a) && isVideo(b) && b.edit) {
    edges = edges.filter((e) => !(e.to === to && isVideo(nodeById(board, e.from))));
  }
  const next = { ...board, edges: [...edges, { id: newId('edge'), from, to }] };
  return reference ? settleFlows(board, next) : next;
}

export function disconnect(board: Board, from: string, to: string): Board {
  return { ...board, edges: board.edges.filter((e) => !(e.from === from && e.to === to)) };
}

export function removeNodes(board: Board, ids: string[]): Board {
  const drop = new Set(ids);
  return {
    ...board,
    nodes: board.nodes.filter((n) => !drop.has(n.id)),
    edges: board.edges.filter((e) => !drop.has(e.from) && !drop.has(e.to)),
  };
}

export type RemovedNode = { id: string; kind: BoardNode['kind']; label?: string };

function nodeLabel(n: BoardNode): string {
  if (n.kind === 'image') return n.phase.label;
  if (n.kind === 'comment') return n.text.slice(0, 42);
  return n.title || '';
}

/**
 * Removes nodes and the storyboard state that depended on them, so nothing stale survives:
 * scenes left outside every flow go back to plain photos (pending keyframes are dropped),
 * flows that lost scenes are retimed, and flows left with no scenes forget their plan.
 */
export function removeNodesClean(board: Board, ids: string[]): { board: Board; removed: RemovedNode[] } {
  const inFlowBefore = new Set(board.nodes.filter(isVideo).flatMap((v) => chainForVideo(board, v.id).map((n) => n.id)));
  let next = removeNodes(board, ids);
  const inFlowAfter = new Set(next.nodes.filter(isVideo).flatMap((v) => chainForVideo(next, v.id).map((n) => n.id)));
  const orphanKeyframes = next.nodes
    .filter(isImage)
    .filter((n) => inFlowBefore.has(n.id) && !inFlowAfter.has(n.id) && !n.phase.imageUrl)
    .map((n) => n.id);
  const removed: RemovedNode[] = [...ids, ...orphanKeyframes]
    .map((id) => nodeById(board, id))
    .filter((n): n is BoardNode => Boolean(n))
    .map((n) => ({ id: n.id, kind: n.kind, label: nodeLabel(n) || undefined }));
  if (orphanKeyframes.length) next = removeNodes(next, orphanKeyframes);
  return { board: settleFlows(board, next), removed };
}

/** Rows top to bottom (half a node of slack), then left to right. */
export function readingOrder<T extends BoardNode>(nodes: T[]): T[] {
  const byY = [...nodes].sort((a, b) => a.y - b.y);
  const rows: T[][] = [];
  for (const n of byY) {
    const row = rows[rows.length - 1];
    if (row && n.y - row[0].y < nodeSize(row[0]).h / 2) row.push(n);
    else rows.push([n]);
  }
  return rows.flatMap((row) => row.sort((a, b) => a.x - b.x));
}

/**
 * Makes the images (in reading order) the whole input flow of a video in one step.
 * Keyframes: each image feeds the next, and the last feeds the video.
 * Reference: every image feeds the video directly, with no links between them.
 * Images of the video's old flow left out of the selection are detached from it; other branches
 * leaving the selected images keep working. The video forgets its old storyboard
 * so the next generation plans over the full set.
 */
export function connectImagesTo(
  board: Board,
  imageIds: string[],
  videoId: string,
  order: 'reading' | 'given' = 'reading',
): { board: Board } | { error: string } {
  const target = nodeById(board, videoId);
  if (!isVideo(target)) return { error: 'Solte as imagens num gerador de vídeo.' };
  if (target.edit) return { error: 'O vídeo editado recebe um vídeo e sons, não imagens.' };
  if (target.fromGallery) return { error: 'Vídeo da galeria já está pronto — ligue-o a outro vídeo para juntar.' };
  if (target.status === 'generating' || target.status === 'joining') return { error: 'Aguarde este vídeo terminar antes de trocar as imagens.' };
  const found = imageIds.map((id) => nodeById(board, id)).filter(isImage);
  const ordered = order === 'given'
    ? found.filter((n, i) => found.findIndex((m) => m.id === n.id) === i)
    : readingOrder(found);
  if (!ordered.length) return { error: 'Selecione imagens para ligar.' };
  const reference = target.imageMode === 'reference';
  const ids = new Set(ordered.map((n) => n.id));
  const prev = new Map(ordered.map((n, i) => [n.id, ordered[i - 1]?.id]));
  const beforeIds = flowImageIds(board, videoId);
  const leaving = new Set(beforeIds.filter((id) => !ids.has(id)));
  const kept = board.edges.filter((e) => {
    if (e.role === 'ref') return true;
    const fromImg = isImage(nodeById(board, e.from));
    if (!fromImg) return true;
    if (e.to === videoId) return reference && ids.has(e.from);
    if (!reference) {
      if (prev.has(e.from) && leaving.has(e.to)) return false;
      return !prev.has(e.to) || prev.get(e.to) === e.from;
    }
    const toImg = isImage(nodeById(board, e.to));
    if (!toImg) return true;
    if (ids.has(e.from) && ids.has(e.to)) return false;
    if ((ids.has(e.from) && leaving.has(e.to)) || (leaving.has(e.from) && ids.has(e.to))) return false;
    return true;
  });
  const has = (from: string, to: string) => kept.some((e) => e.from === from && e.to === to);
  const links = reference
    ? ordered.map((n) => ({ from: n.id, to: videoId }))
    : [...ordered.slice(1).map((n, i) => ({ from: ordered[i].id, to: n.id })), { from: ordered[ordered.length - 1].id, to: videoId }];
  const added: BoardEdge[] = links
    .filter((e) => !has(e.from, e.to))
    .map((e) => ({ id: newId('edge'), ...e }));
  let next = settleFlows(board, { ...board, edges: [...kept, ...added] });
  const same = beforeIds.length === ordered.length && ordered.every((n) => beforeIds.includes(n.id));
  if (!same) {
    next = {
      ...next,
      nodes: next.nodes.map((n) => (n.id === videoId && isVideo(n)
        ? { ...n, planned: undefined, partUrls: undefined, partJobIds: undefined, error: undefined, ...(n.status === 'failed' ? { status: 'idle' as const } : {}) }
        : n)),
    };
  }
  return { board: next };
}

/**
 * Arrows a storyboard should draw.
 * Contínuo: each scene feeds the next, and the last feeds the video.
 * Referências: every scene feeds the video; nothing sits between them.
 */
export function storyboardEdges(mode: BoardImageMode, phaseIds: string[], videoId: string): Array<[string, string]> {
  if (mode === 'reference') return phaseIds.map((id) => [id, videoId]);
  return phaseIds.map((id, i) => [id, phaseIds[i + 1] || videoId]);
}

/**
 * Switches how the video uses its images and rewires them to match.
 * Referências has no place in time for an ungenerated keyframe, so those cards are removed.
 */
export function applyImageMode(board: Board, videoId: string, mode: BoardImageMode): Board {
  const target = nodeById(board, videoId);
  if (!isVideo(target) || target.edit || target.fromGallery) return board;
  if ((target.imageMode || 'keyframes') === mode) return board;
  let images = chainForVideo(board, videoId).map((n) => n.id);
  let flagged: Board = {
    ...board,
    nodes: board.nodes.map((n) => (n.id === videoId && isVideo(n)
      ? { ...n, imageMode: mode, partUrls: undefined, partJobIds: undefined }
      : n)),
  };
  if (mode === 'reference') {
    const drop = images.filter((id) => {
      const n = nodeById(flagged, id);
      return isImage(n) && !n.phase.imageUrl;
    });
    if (drop.length) flagged = removeNodes(flagged, drop);
    images = images.filter((id) => !drop.includes(id));
    if (images.length === 1) return connect(flagged, images[0], videoId);
  }
  if (images.length < 2) return flagged;
  const wired = connectImagesTo(flagged, images, videoId, 'given');
  return 'error' in wired ? flagged : wired.board;
}

/** After edges or nodes change: retime flows that lost or gained scenes, reset scenes left outside every flow. */
function settleFlows(before: Board, next: Board): Board {
  const inFlowBefore = new Set(before.nodes.filter(isVideo).flatMap((v) => chainForVideo(before, v.id).map((n) => n.id)));
  const chainBefore = new Map(before.nodes.filter(isVideo).map((v) => [v.id, chainForVideo(before, v.id).map((n) => n.id)]));
  const inFlowAfter = new Set(next.nodes.filter(isVideo).flatMap((v) => chainForVideo(next, v.id).map((n) => n.id)));
  const retimed = new Map<string, FragmentadorSessionPhase>();
  const plan = new Map<string, Partial<BoardVideoNode>>();
  for (const v of next.nodes.filter(isVideo)) {
    const before = chainBefore.get(v.id) || [];
    const chain = chainForVideo(next, v.id);
    if (chain.length === before.length && chain.every((n, i) => n.id === before[i])) continue;
    if (!chain.length) {
      plan.set(v.id, {
        planned: undefined,
        brief: undefined,
        look: undefined,
        intentHint: undefined,
        totalDurationSeconds: undefined,
        partUrls: undefined,
        partJobIds: undefined,
        error: undefined,
        ...(v.versions?.length || v.clipUrl ? {} : { status: 'idle' as const, progress: undefined }),
      });
      continue;
    }
    let t = 0;
    for (const n of chain) {
      const d = Math.max(1, Math.round(n.phase.t_end - n.phase.t_start) || 2);
      retimed.set(n.id, { ...n.phase, t_start: t, t_end: t + d });
      t += d;
    }
    plan.set(v.id, { totalDurationSeconds: t, partUrls: undefined, partJobIds: undefined });
  }

  return {
    ...next,
    nodes: next.nodes.map((n) => {
      if (isImage(n)) {
        if (retimed.has(n.id)) return { ...n, phase: retimed.get(n.id)! };
        if (!inFlowBefore.has(n.id) || inFlowAfter.has(n.id)) return n;
        const blank = createEmptyPhase(0);
        return {
          ...n,
          phase: {
            ...blank,
            id: n.phase.id,
            label: n.phase.label,
            description_pt: n.phase.label,
            still_prompt_en: '',
            imageUrl: n.phase.imageUrl,
            status: 'ready',
          },
        };
      }
      if (isVideo(n) && plan.has(n.id)) return { ...n, ...plan.get(n.id) };
      return n;
    }),
  };
}

function incomingImage(board: Board, id: string, exclude: Set<string>): BoardImageNode | undefined {
  const target = nodeById(board, id);
  const sources = board.edges
    .filter((e) => isFlowEdge(e) && e.to === id)
    .map((e) => nodeById(board, e.from))
    .filter((n): n is BoardImageNode => isImage(n) && !exclude.has(n.id));
  if (sources.length <= 1 || !target) return sources[0];
  return sources.sort((p, q) => Math.abs(p.y - target.y) - Math.abs(q.y - target.y))[0];
}

function keyframeChain(board: Board, videoId: string): BoardImageNode[] {
  const out: BoardImageNode[] = [];
  const seen = new Set<string>();
  let cur = incomingImage(board, videoId, seen);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = incomingImage(board, cur.id, seen);
  }
  return out;
}

/** Every image that reaches `rootId` by following arrows backward, including a chain the reference mode would ignore. */
export function ancestorImages(board: Board, rootId: string): BoardImageNode[] {
  const out: BoardImageNode[] = [];
  const seen = new Set<string>();
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    for (const e of board.edges) {
      if (!isFlowEdge(e) || e.to !== id || seen.has(e.from)) continue;
      const n = nodeById(board, e.from);
      if (!isImage(n)) continue;
      seen.add(n.id);
      out.push(n);
      queue.push(n.id);
    }
  }
  return out;
}

function directImages(board: Board, videoId: string): BoardImageNode[] {
  return board.edges
    .filter((e) => isFlowEdge(e) && e.to === videoId)
    .map((e) => nodeById(board, e.from))
    .filter(isImage);
}

/** Images that currently feed the video, whether chained or linked straight to it. */
function flowImageIds(board: Board, videoId: string): string[] {
  const ids = new Set<string>();
  for (const n of keyframeChain(board, videoId)) ids.add(n.id);
  for (const n of directImages(board, videoId)) ids.add(n.id);
  return Array.from(ids);
}

/** Stage cards from Explore templates: "Stage 1", "Stage 2", … */
export function stageOrderIndex(label: string | undefined): number {
  const match = /^Stage\s+(\d+)$/i.exec(String(label || '').trim());
  return match ? Number(match[1]) : 0;
}

/**
 * Reference-mode listing: model stages in chronological order, then any other
 * flow photos in reading order. Without stages, falls back to reading order.
 */
export function chronologicalReferenceImages(images: BoardImageNode[]): BoardImageNode[] {
  if (images.length <= 1) return images;
  const stages = images
    .filter((node) => stageOrderIndex(node.phase.label) > 0)
    .sort((a, b) => stageOrderIndex(a.phase.label) - stageOrderIndex(b.phase.label));
  if (!stages.length) return readingOrder(images);
  const stageIds = new Set(stages.map((node) => node.id));
  const rest = readingOrder(images.filter((node) => !stageIds.has(node.id)));
  return [...stages, ...rest];
}

/**
 * Ordered image nodes feeding a video node.
 * Keyframes: the chain ending at the video (start → end).
 * Reference: every image linked straight to the video — stages chronologically
 * when present, otherwise reading order.
 */
export function chainForVideo(board: Board, videoId: string): BoardImageNode[] {
  const video = nodeById(board, videoId);
  if (isVideo(video) && video.imageMode === 'reference') {
    return chronologicalReferenceImages(directImages(board, videoId));
  }
  return keyframeChain(board, videoId);
}

/** Video nodes linked into this one, top-to-bottom then left-to-right. */
export function upstreamVideos(board: Board, videoId: string): BoardVideoNode[] {
  const target = nodeById(board, videoId);
  const order = isVideo(target) ? target.joinOrder || [] : [];
  const rank = (id: string) => {
    const i = order.indexOf(id);
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  return board.edges
    .filter((e) => e.to === videoId)
    .map((e) => nodeById(board, e.from))
    .filter(isVideo)
    .sort((a, b) => rank(a.id) - rank(b.id) || a.y - b.y || a.x - b.x);
}

export function downstreamVideos(board: Board, videoId: string): BoardVideoNode[] {
  return board.edges
    .filter((e) => e.from === videoId)
    .map((e) => nodeById(board, e.to))
    .filter(isVideo);
}

/** Clip URLs in play order for a video node; null if some input is not ready. */
export function videoSequence(board: Board, videoId: string, seen = new Set<string>()): string[] | null {
  if (seen.has(videoId)) return [];
  seen.add(videoId);
  const node = nodeById(board, videoId);
  if (!isVideo(node)) return null;
  if (node.edit) return node.videoUrl ? [node.videoUrl] : null;
  const urls: string[] = [];
  for (const up of upstreamVideos(board, videoId)) {
    const seq = videoSequence(board, up.id, seen);
    if (!seq) return null;
    urls.push(...seq);
  }
  const hasOwnFlow = node.fromGallery || chainForVideo(board, videoId).length > 0 || Boolean(node.versions?.length);
  if (hasOwnFlow) {
    if (!node.clipUrl) return null;
    urls.push(node.clipUrl);
  }
  return urls;
}

/**
 * What a join with a transition plays: each direct input's own result (not flattened into clips,
 * so cuts inside an upstream join keep their style), then its own clip. Null if an input is not ready.
 */
export function joinUnits(board: Board, videoId: string): string[] | null {
  const node = nodeById(board, videoId);
  if (!isVideo(node) || node.edit) return null;
  const urls: string[] = [];
  for (const up of upstreamVideos(board, videoId)) {
    if (!videoSequence(board, up.id) || !up.videoUrl) return null;
    urls.push(up.videoUrl);
  }
  const hasOwnFlow = node.fromGallery || chainForVideo(board, videoId).length > 0 || Boolean(node.versions?.length);
  if (hasOwnFlow) {
    if (!node.clipUrl) return null;
    urls.push(node.clipUrl);
  }
  return urls;
}

/** The join a request about "the videos before the join" targets: itself, the source of an edit, or the next join downstream. */
export function joinNodeFor(board: Board, videoId: string): BoardVideoNode | undefined {
  const seen = new Set<string>();
  let node = nodeById(board, videoId);
  while (isVideo(node) && !seen.has(node.id)) {
    seen.add(node.id);
    if (node.edit) {
      node = upstreamVideos(board, node.id)[0];
      continue;
    }
    if (upstreamVideos(board, node.id).length) return node;
    node = downstreamVideos(board, node.id).find((d) => !d.edit);
  }
  return undefined;
}

/** Image nodes with no incoming or outgoing edge, left-to-right. */
export function looseImages(board: Board): BoardImageNode[] {
  return board.nodes
    .filter(isImage)
    .filter((n) => !board.edges.some((e) => isFlowEdge(e) && (e.from === n.id || e.to === n.id)))
    .sort((a, b) => a.x - b.x || a.y - b.y);
}

export function videoForImage(board: Board, imageId: string): BoardVideoNode | undefined {
  return board.nodes.filter(isVideo).find((v) => chainForVideo(board, v.id).some((n) => n.id === imageId));
}

/**
 * Ready images the user selected that do not feed a video.
 * Empty when a video is selected, or when any selected image is already in a flow.
 */
export function standaloneSelectedImages(board: Board, selectedIds: string[]): BoardImageNode[] {
  const picked = new Set(selectedIds);
  if (board.nodes.some((n) => isVideo(n) && picked.has(n.id))) return [];
  const images = board.nodes.filter(isImage).filter((n) =>
    picked.has(n.id) && Boolean(n.phase.imageUrl) && n.phase.status === 'ready',
  );
  if (!images.length || images.some((n) => videoForImage(board, n.id))) return [];
  return images;
}

/**
 * An edit of a loose selection stays on those images.
 * A storyboard elsewhere on the board does not become the target, and the video is not regenerated.
 */
export function narrowStandaloneImageEdit<T extends {
  node_ids: string[];
  reference_node_ids?: string[];
  regenerate_video_id?: string;
}>(board: Board, selectedIds: string[], intent: T): T {
  const alone = standaloneSelectedImages(board, selectedIds);
  if (!alone.length) return intent;
  const nodeIds = alone.map((n) => n.id);
  const keep = new Set(nodeIds);
  const refs = (intent.reference_node_ids || []).filter((id) => selectedIds.includes(id) && !keep.has(id));
  return {
    ...intent,
    node_ids: nodeIds,
    reference_node_ids: refs.length ? refs : undefined,
    regenerate_video_id: undefined,
  };
}

/**
 * Storyboard phases must reuse the photos already on the board.
 * A planned phase that carries one of those pictures is that node, even when the planner minted a new id.
 * Without new keyframes, a repeated photo is dropped instead of becoming a second copy.
 */
export function bindStoryboardPhases(
  phases: FragmentadorSessionPhase[],
  images: BoardImageNode[],
  keyframes: boolean,
): FragmentadorSessionPhase[] {
  const used = new Set<string>();
  const claim = (node: BoardImageNode | undefined): BoardImageNode | undefined => {
    if (!node || used.has(node.id)) return undefined;
    used.add(node.id);
    return node;
  };
  const bound: FragmentadorSessionPhase[] = [];
  for (const phase of phases) {
    const host = claim(images.find((n) => n.id === phase.id))
      || claim(images.find((n) => n.phase.id === phase.id))
      || (phase.imageUrl ? claim(images.find((n) => n.phase.imageUrl === phase.imageUrl)) : undefined);
    if (host) {
      bound.push({
        ...phase,
        id: host.id,
        label: host.phase.label || phase.label,
        imageUrl: host.phase.imageUrl || phase.imageUrl,
        status: (host.phase.imageUrl || phase.imageUrl) ? 'ready' : phase.status,
      });
      continue;
    }
    if (!keyframes) continue;
    if (phase.imageUrl) {
      bound.push({ ...phase, imageUrl: undefined, status: 'pending', jobId: undefined });
      continue;
    }
    bound.push(phase);
  }
  const photos = images.filter((n) => n.phase.imageUrl);
  if (!keyframes && bound.length < Math.min(2, photos.length)) {
    return photos.map((n) => ({ ...n.phase, id: n.id, label: n.phase.label, status: 'ready' as const }));
  }
  return bound;
}

/**
 * A script that is already exactly these photos.
 * Reusing it avoids planning the same storyboard again.
 */
export function plannedVideoForPhotos(board: Board, photoIds: string[]): string | undefined {
  if (photoIds.length < 2) return undefined;
  const videos = photoIds.map((id) => videoForImage(board, id));
  const first = videos[0];
  if (!first?.planned || videos.some((v) => v?.id !== first.id)) return undefined;
  const chain = chainForVideo(board, first.id);
  if (chain.length !== photoIds.length) return undefined;
  const ids = new Set(chain.map((n) => n.id));
  return photoIds.every((id) => ids.has(id)) ? first.id : undefined;
}

/** Scene images of the storyboard this video plays, in order (every trecho of a split storyboard). */
export function storyboardImages(board: Board, videoId: string): BoardImageNode[] {
  const node = nodeById(board, videoId);
  if (!isVideo(node)) return [];
  const ups = upstreamVideos(board, videoId);
  const group = node.flowGroup || ups.find((u) => u.flowGroup)?.flowGroup;
  if (group) return flowGroupNodes(board, group).flows.flatMap((f) => chainForVideo(board, f.id));
  const own = chainForVideo(board, videoId);
  if (own.length) return own;
  return ups.length === 1 ? storyboardImages(board, ups[0].id) : [];
}

/** Free spot below existing content for new rows (parallel flows). */
export function nextRowY(board: Board): number {
  if (!board.nodes.length) return 120;
  return Math.max(...board.nodes.map((n) => n.y + nodeSize(n, board.aspectRatio).h)) + 80;
}

/** Seconds the script fixed. Undefined when the card may still choose a duration. */
export function scriptOwnedSeconds(planned: boolean | undefined, seconds: number | undefined): number | undefined {
  if (!planned) return undefined;
  const n = Math.round(Number(seconds) || 0);
  return n > 0 ? n : undefined;
}

/** Hover copy for a duration the script already chose. */
export function scriptDurationTitle(seconds: number): string {
  const n = Math.round(seconds);
  return `O roteiro escolheu ${n} ${n === 1 ? 'segundo' : 'segundos'}`;
}

/** Identity of the script: play order and the exact photo on each node. */
export function storyScriptKey(images: BoardImageNode[]): string {
  return images.map((n) => `${n.id}@${n.phase.imageUrl || 'pending'}`).join('|');
}

export function imageCaption(n: BoardImageNode): string | undefined {
  return n.caption && n.phase.imageUrl && n.captionUrl === n.phase.imageUrl ? n.caption : undefined;
}

export function boardSummaryForAgent(board: Board, selectedIds: string[]) {
  const videos = board.nodes.filter(isVideo);
  return {
    aspect_ratio: board.aspectRatio,
    nodes: board.nodes.flatMap((n) =>
      n.kind === 'comment'
        ? []
        : n.kind === 'image'
        ? {
            id: n.id,
            kind: 'image',
            label: n.phase.label,
            x: n.x,
            y: n.y,
            has_image: Boolean(n.phase.imageUrl) && n.phase.status === 'ready',
            status: n.phase.status,
            description: n.phase.description_pt,
            content: imageCaption(n),
            width: n.width,
            height: n.height,
            ...(imageVersions(n).length > 1
              ? { versions: imageVersions(n).length, version: imageVersions(n).findIndex((v) => v.imageUrl === n.phase.imageUrl) + 1 }
              : {}),
          }
        : n.kind === 'audio'
        ? {
            id: n.id,
            kind: 'audio',
            audio_kind: n.audioKind,
            label: n.title || 'Som',
            x: n.x,
            y: n.y,
            status: n.status,
            text: n.text.slice(0, 600),
            has_audio: Boolean(n.audioUrl),
            seconds: n.seconds ? Math.round(n.seconds * 10) / 10 : undefined,
            volume: n.volume,
            start_offset: n.startOffset,
            loop: Boolean(n.loop),
            muted: Boolean(n.muted),
            speed: n.speed || 1,
            versions: audioVersions(n).length,
          }
        : {
            id: n.id,
            kind: 'video',
            label: n.title || 'Vídeo',
            x: n.x,
            y: n.y,
            status: n.status,
            ...(n.fromGallery ? { from_gallery: true } : {}),
          },
    ),
    edges: board.edges.map((e) => ({ from: e.from, to: e.to, ...(e.role === 'ref' ? { role: 'ref' as const } : {}) })),
    videos: videos.map((v) => ({
      id: v.id,
      status: v.status,
      /**
       * trecho = one hard-cut segment of a split storyboard; junção = plays its upstream videos in order;
       * edição = its one upstream video mixed with the linked audio nodes.
       */
      role: v.edit
        ? 'edição'
        : chainForVideo(board, v.id).length
        ? v.flowGroup ? 'trecho' : 'fluxo'
        : upstreamVideos(board, v.id).length ? 'junção' : 'vídeo',
      chain: chainForVideo(board, v.id).map((n) => n.id),
      upstream_videos: upstreamVideos(board, v.id).map((u) => u.id),
      planned: Boolean(v.planned),
      has_result: Boolean(v.videoUrl),
      seconds: Math.round(videoSeconds(board, v.id) * 10) / 10 || undefined,
      ...(v.generator?.prompt || v.brief ? { prompt: (v.generator?.prompt || v.brief || '').slice(0, 400) } : {}),
      ...(v.generator?.hidePrompt || v.templateJobId ? { from_template: true } : {}),
      ...(v.generator?.startImage?.url ? { has_start_image: true } : {}),
      ...(v.generator?.endImage?.url ? { has_end_image: true } : {}),
      ...(v.generator?.attachments?.length ? { attachments: v.generator.attachments.length } : {}),
      ...(v.generator?.referenceVideo?.url ? { has_reference_video: true } : {}),
      ...(v.generator?.audio?.url ? { has_audio_input: true } : {}),
      ...(v.joinTransition ? { join_transition: v.joinTransition.kind, join_transition_seconds: v.joinTransition.seconds } : {}),
      ...(!v.edit && !v.fromGallery ? { image_mode: v.imageMode || 'keyframes' } : {}),
      ...(v.edit
        ? {
            audio_inputs: audioInputs(board, v.id).map((a) => a.id),
            source_volume: v.edit.sourceVolume,
            captions: Boolean(v.edit.captions),
            caption_position: v.edit.captionPosition || 'bottom',
            fade: Boolean(v.edit.fade),
            grade: Boolean(v.edit.grade),
            grayscale: Boolean(v.edit.grayscale),
          }
        : {}),
    })),
    selected_ids: selectedIds.filter((id) => !isComment(nodeById(board, id))),
  };
}

const BOARD_KEY = 'seu.video.telaEmBranco.board';
const THREAD_KEY = 'seu.video.telaEmBranco.thread';
const PROJECTS_KEY = 'seu.video.telaEmBranco.projects';
const ACTIVE_PROJECT_KEY = 'seu.video.telaEmBranco.activeProject';

function scopedKey(base: string, orgId: string) {
  return `${base}.${orgId || 'anon'}`;
}

function projectKey(base: string, orgId: string, projectId: string) {
  return `${scopedKey(base, orgId)}.${projectId}`;
}

// ── Projects (one board + chat each) ─────────────────────────

export type CanvasProjectMeta = {
  id: string;
  title: string;
  /** User renamed it; stop deriving the title from the chat. */
  customTitle?: boolean;
  createdAt: number;
  updatedAt: number;
  thumbUrl?: string;
  thumbKind?: 'image' | 'video';
  scenes: number;
  videos: number;
  /** The owner shared it with the company (a synced copy lives in `canvas_projects`). */
  shared?: boolean;
};

export const UNTITLED_PROJECT = 'Projeto sem título';

export function createProjectMeta(title = UNTITLED_PROJECT): CanvasProjectMeta {
  const now = Date.now();
  return { id: newId('proj'), title, createdAt: now, updatedAt: now, scenes: 0, videos: 0 };
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota */
  }
}

export function saveProjects(orgId: string, projects: CanvasProjectMeta[]) {
  writeJson(scopedKey(PROJECTS_KEY, orgId), projects);
}

/** Project list written by another tab (`storage` event), or null if the event is unrelated. */
export function projectsFromStorageEvent(orgId: string, e: StorageEvent): CanvasProjectMeta[] | null {
  if (e.key !== scopedKey(PROJECTS_KEY, orgId) || !e.newValue) return null;
  try {
    const list = JSON.parse(e.newValue);
    return Array.isArray(list) ? list.filter((p) => p && typeof p.id === 'string') : null;
  } catch {
    return null;
  }
}

export function saveActiveProjectId(orgId: string, projectId: string) {
  try {
    localStorage.setItem(scopedKey(ACTIVE_PROJECT_KEY, orgId), projectId);
  } catch {
    /* private mode / quota */
  }
}

export function readActiveProjectId(orgId: string): string {
  try {
    return localStorage.getItem(scopedKey(ACTIVE_PROJECT_KEY, orgId)) || '';
  } catch {
    return '';
  }
}

/** Project list + active id. The pre-projects single board becomes the first project. */
export function ensureProjects(orgId: string): { projects: CanvasProjectMeta[]; activeId: string } {
  const stored = readJson<CanvasProjectMeta[]>(scopedKey(PROJECTS_KEY, orgId));
  let projects = Array.isArray(stored) ? stored.filter((p) => p && typeof p.id === 'string') : [];
  if (!projects.length) {
    const first = createProjectMeta('Projeto 1');
    try {
      for (const base of [BOARD_KEY, THREAD_KEY]) {
        const legacy = localStorage.getItem(scopedKey(base, orgId));
        if (legacy == null) continue;
        localStorage.setItem(projectKey(base, orgId, first.id), legacy);
        localStorage.removeItem(scopedKey(base, orgId));
      }
    } catch {
      /* private mode / quota */
    }
    projects = [first];
    saveProjects(orgId, projects);
  }
  let activeId = '';
  try {
    activeId = localStorage.getItem(scopedKey(ACTIVE_PROJECT_KEY, orgId)) || '';
  } catch {
    activeId = '';
  }
  if (!projects.some((p) => p.id === activeId)) {
    activeId = [...projects].sort((a, b) => b.updatedAt - a.updatedAt)[0].id;
    saveActiveProjectId(orgId, activeId);
  }
  return { projects, activeId };
}

export function deleteProjectData(orgId: string, projectId: string) {
  try {
    localStorage.removeItem(projectKey(BOARD_KEY, orgId, projectId));
    localStorage.removeItem(projectKey(THREAD_KEY, orgId, projectId));
  } catch {
    /* private mode / quota */
  }
}

export function boardIsEmpty(board: Board): boolean {
  return board.nodes.length === 0;
}

/** Gallery URLs of videos this project generated (not clips dragged in from the gallery). */
export function projectGeneratedVideoUrls(board: Board): string[] {
  const urls = new Set<string>();
  for (const n of board.nodes) {
    if (n.kind !== 'video') continue;
    if (!n.fromGallery && n.clipUrl) urls.add(n.clipUrl);
    for (const v of videoVersions(n)) urls.add(v.clipUrl);
    if (n.videoUrl && !(n.fromGallery && n.videoUrl === n.clipUrl)) urls.add(n.videoUrl);
  }
  return Array.from(urls).filter((u) => /^https?:\/\//i.test(u) || u.startsWith('media/'));
}

/** Refresh title (from the first user message), thumbnail and counts from the project's content. */
export function projectMetaFromContent(
  meta: CanvasProjectMeta,
  board: Board,
  firstUserText: string | undefined,
  touch: boolean,
): CanvasProjectMeta {
  const images = board.nodes.filter((n): n is BoardImageNode => n.kind === 'image');
  const videos = board.nodes.filter((n): n is BoardVideoNode => n.kind === 'video');
  const isHttp = (u?: string) => Boolean(u && /^https?:\/\//i.test(u));
  const readyVideo = videos.find((v) => isHttp(v.videoUrl));
  const firstImage = images
    .slice()
    .sort((a, b) => a.x - b.x || a.y - b.y)
    .find((n) => isHttp(n.phase.imageUrl));
  const thumb = firstImage
    ? { thumbUrl: firstImage.phase.imageUrl, thumbKind: 'image' as const }
    : readyVideo
    ? { thumbUrl: readyVideo.videoUrl, thumbKind: 'video' as const }
    : { thumbUrl: undefined, thumbKind: undefined };
  const derived = (firstUserText || '').replace(/\s+/g, ' ').trim();
  const title = meta.customTitle
    ? meta.title
    : derived
    ? derived.length > 48 ? `${derived.slice(0, 47).trimEnd()}…` : derived
    : meta.title;
  return {
    ...meta,
    ...thumb,
    title,
    scenes: images.length,
    videos: videos.reduce((sum, v) => sum + Math.max(1, videoVersions(v).length), 0),
    updatedAt: touch ? Date.now() : meta.updatedAt,
  };
}

export function loadBoard(orgId: string, projectId: string): Board {
  try {
    const raw = localStorage.getItem(projectKey(BOARD_KEY, orgId, projectId));
    if (!raw) return emptyBoard();
    const parsed = JSON.parse(raw) as Board;
    if (!Array.isArray(parsed?.nodes) || !Array.isArray(parsed?.edges)) return emptyBoard();
    parsed.nodes = parsed.nodes.map((n) => {
      if (n.kind === 'video' && (n.status === 'generating' || n.status === 'joining')) {
        // Jobs already queued (or every part rendered) are picked up again by the page; see `videoResumable`.
        // A render still only in this tab, before any job id, is kept by canvasLiveBoard and never reloaded here.
        if (n.status === 'generating' && videoResumable(n)) return { ...n, progress: 'Retomando…', error: undefined };
        // A join/mix is rebuilt from its inputs when the page resumes.
        if (n.status === 'joining') return { ...n, progress: 'Retomando…', error: undefined };
        return { ...n, status: n.videoUrl ? 'ready' : 'failed', progress: undefined, error: n.videoUrl ? undefined : 'Interrompido — peça para gerar de novo.' };
      }
      if (n.kind === 'image' && n.phase.status === 'generating') {
        if (!n.phase.imageUrl && n.phase.jobId) return { ...n, phase: { ...n.phase, error: 'Retomando…' } };
        return { ...n, phase: { ...n.phase, status: n.phase.imageUrl ? 'ready' : 'pending', error: undefined } };
      }
      if (n.kind === 'audio' && n.status === 'generating') {
        return { ...n, status: n.audioUrl ? 'ready' : 'failed', progress: undefined, error: n.audioUrl ? undefined : 'Interrompido — peça para gerar de novo.' };
      }
      return n;
    });
    return { ...emptyBoard(), ...parsed };
  } catch {
    return emptyBoard();
  }
}

/** A flow left mid-generation that the page can finish without a new request: queued jobs or all parts rendered. */
export function videoResumable(n: BoardVideoNode): boolean {
  if (n.clipUrl || n.edit || n.fromGallery) return false;
  if (n.remixJobId) return true;
  const jobs = n.partJobIds || [];
  const urls = n.partUrls || [];
  if (jobs.length && jobs.every((j, i) => Boolean(j) || Boolean(urls[i]))) return true;
  return urls.length > 0 && urls.every(Boolean);
}

export function saveBoard(orgId: string, projectId: string, board: Board) {
  writeJson(projectKey(BOARD_KEY, orgId, projectId), board);
}

/** How many conversations one project keeps. */
export const CHAT_TAB_MAX = 12;

export type CanvasChatTab<T> = {
  id: string;
  createdAt: number;
  messages: T[];
};

/** Every conversation of a project, plus which one is on screen. */
export type CanvasChatBook<T> = {
  activeId: string;
  chats: CanvasChatTab<T>[];
};

export function chatHasContent(messages: Array<{ text?: string; contextBreak?: boolean }> | undefined): boolean {
  return Boolean(messages?.some((m) => Boolean(m && (String(m.text || '').trim() || m.contextBreak))));
}

/** Short name for a tab: the first thing the user asked, or "Novo". */
export function chatTabLabel(messages: Array<{ role?: string; text?: string; formAnswer?: boolean }> | undefined): string {
  const text = messages?.find((m) => m && m.role === 'user' && !m.formAnswer && String(m.text || '').trim())?.text || '';
  const clean = String(text).replace(/\s+/g, ' ').trim();
  if (!clean) return 'Novo';
  if (clean.length <= 24) return clean;
  const cut = clean.slice(0, 23);
  const space = cut.lastIndexOf(' ');
  const base = space > 10 ? cut.slice(0, space) : cut;
  return `${base.trimEnd()}…`;
}

export function activeChatMessages<T>(book: CanvasChatBook<T>): T[] {
  return book.chats.find((c) => c.id === book.activeId)?.messages || book.chats[0]?.messages || [];
}

/** Accepts the current book, or the legacy single-thread array. */
export function ensureChatBook<T>(raw: unknown): CanvasChatBook<T> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const body = raw as { activeId?: unknown; chats?: unknown };
    if (Array.isArray(body.chats)) {
      const chats: CanvasChatTab<T>[] = [];
      for (const item of body.chats) {
        if (!item || typeof item !== 'object') continue;
        const row = item as { id?: unknown; createdAt?: unknown; messages?: unknown };
        if (typeof row.id !== 'string' || !Array.isArray(row.messages)) continue;
        chats.push({
          id: row.id,
          createdAt: typeof row.createdAt === 'number' ? row.createdAt : Date.now(),
          messages: (row.messages as T[]).slice(-40),
        });
        if (chats.length >= CHAT_TAB_MAX) break;
      }
      if (chats.length) {
        const activeId = typeof body.activeId === 'string' && chats.some((c) => c.id === body.activeId)
          ? body.activeId
          : chats[chats.length - 1].id;
        return { activeId, chats };
      }
    }
  }
  if (Array.isArray(raw)) {
    const chat: CanvasChatTab<T> = { id: newId('chat'), createdAt: Date.now(), messages: (raw as T[]).slice(-40) };
    return { activeId: chat.id, chats: [chat] };
  }
  const chat: CanvasChatTab<T> = { id: newId('chat'), createdAt: Date.now(), messages: [] };
  return { activeId: chat.id, chats: [chat] };
}

export function replaceActiveChat<T>(book: CanvasChatBook<T>, messages: T[]): CanvasChatBook<T> {
  if (!book.chats.length) return ensureChatBook<T>(messages);
  const activeId = book.chats.some((c) => c.id === book.activeId) ? book.activeId : book.chats[book.chats.length - 1].id;
  const next = messages.slice(-40);
  return {
    activeId,
    chats: book.chats.map((c) => (c.id === activeId ? { ...c, messages: next } : c)),
  };
}

/** Opens a blank conversation. The current one stays as its own tab. */
export function openNewChat<T>(book: CanvasChatBook<T>): CanvasChatBook<T> {
  const active = book.chats.find((c) => c.id === book.activeId) || book.chats[0];
  if (!active || !chatHasContent(active.messages as Array<{ text?: string; contextBreak?: boolean }>)) return book;
  if (book.chats.length >= CHAT_TAB_MAX) return book;
  const chat: CanvasChatTab<T> = { id: newId('chat'), createdAt: Date.now(), messages: [] };
  return { activeId: chat.id, chats: [...book.chats, chat] };
}

export function selectChat<T>(book: CanvasChatBook<T>, id: string): CanvasChatBook<T> {
  if (book.activeId === id || !book.chats.some((c) => c.id === id)) return book;
  return { ...book, activeId: id };
}

/** Drops one conversation. The last one stays. */
export function closeChat<T>(book: CanvasChatBook<T>, id: string): CanvasChatBook<T> {
  if (book.chats.length <= 1 || !book.chats.some((c) => c.id === id)) return book;
  const index = book.chats.findIndex((c) => c.id === id);
  const chats = book.chats.filter((c) => c.id !== id);
  const activeId = book.activeId === id ? chats[Math.min(index, chats.length - 1)].id : book.activeId;
  return { activeId, chats };
}

export function loadChatBook<T>(orgId: string, projectId: string): CanvasChatBook<T> {
  return ensureChatBook<T>(readJson<unknown>(projectKey(THREAD_KEY, orgId, projectId)));
}

export function saveChatBook<T>(orgId: string, projectId: string, book: CanvasChatBook<T>) {
  writeJson(projectKey(THREAD_KEY, orgId, projectId), ensureChatBook<T>(book));
}

/** Board or chat of `projectId` written by another tab (`storage` event). */
export function projectDataFromStorageEvent<T>(
  orgId: string,
  projectId: string,
  e: StorageEvent,
): { board: Board } | { thread: T[]; book: CanvasChatBook<T> } | null {
  if (!e.newValue) return null;
  try {
    if (e.key === projectKey(BOARD_KEY, orgId, projectId)) {
      const parsed = JSON.parse(e.newValue) as Board;
      if (!Array.isArray(parsed?.nodes) || !Array.isArray(parsed?.edges)) return null;
      return { board: { ...emptyBoard(), ...parsed } };
    }
    if (e.key === projectKey(THREAD_KEY, orgId, projectId)) {
      const book = ensureChatBook<T>(JSON.parse(e.newValue));
      return { thread: activeChatMessages(book), book };
    }
  } catch {
    return null;
  }
  return null;
}

export function loadThread<T>(orgId: string, projectId: string): T[] {
  return activeChatMessages(loadChatBook<T>(orgId, projectId));
}

export function saveThread<T>(orgId: string, projectId: string, thread: T[]) {
  saveChatBook(orgId, projectId, replaceActiveChat(loadChatBook<T>(orgId, projectId), thread));
}
